import qs from 'qs';
import { snakeCase, camelCase, sortBy, isEqual, reduce, merge } from 'lodash';
import { dayOnlyDateFormat, DateTime, toDateTimeObj } from 'common/utils/date';

export const NOT_TRANSLATABLE_OPTIONS = [
  'tags',
  'teams',
  'questions',
  'channels',
];

const DATE_PARAMS = ['start_date', 'end_date', 'startDate', 'endDate'];

const queryToFilters = filters =>
  Object.keys(filters).reduce((accum, key) => {
    const value = filters[key];
    const isDate = DATE_PARAMS.includes(key);
    const newKey = isDate ? camelCase(key) : key;
    let newValue = isDate ? toDateTimeObj(value) : value;
    newValue = isEqual(newValue, ['']) ? [] : newValue;
    return Object.assign({}, accum, { [newKey]: newValue });
  }, {});

const parse = queryString => {
  const obj = qs.parse(queryString, { ignoreQueryPrefix: true });
  return queryToFilters(obj);
};

const withFormattedDates = filters =>
  Object.keys(filters).reduce((keyValues, key) => {
    const value = filters[key];
    const isDate = DATE_PARAMS.includes(key);
    let newValue;
    newValue = isDate && value ? value.toFormat(dayOnlyDateFormat) : value;
    newValue = isEqual(newValue, []) ? [''] : newValue;
    const newKey = isDate ? snakeCase(key) : key;
    return Object.assign({}, keyValues, { [newKey]: newValue });
  }, {});

const filtersToSearch = filters =>
  qs.stringify(withFormattedDates(filters), {
    addQueryPrefix: true,
    encodeValuesOnly: true,
    arrayFormat: 'brackets',
  });

const updateSearch = (previousSearch, filtersToMerge) => {
  const previousFilters = parse(previousSearch);
  const newSearch = { ...previousFilters, ...filtersToMerge };

  return { search: filtersToSearch(newSearch) };
};

const objectToOptions = (key, obj) => {
  const optionsList = sortBy(
    Object.keys(obj).map(optionKey =>
      merge(
        { value: optionKey, text: obj[optionKey] },
        NOT_TRANSLATABLE_OPTIONS.includes(key)
          ? { 'data-notranslate': true }
          : {}
      )
    ),
    ['text']
  );
  return optionsList;
};

const utcToLocal = (dayOfWeek, sendAt, snakedKeys = false) => {
  const localTime = DateTime.now()
    .toUTC()
    .set({
      weekday: dayOfWeek === 0 ? 7 : dayOfWeek,
      hour: sendAt,
      minute: 0,
      second: 0,
      millisecond: 0,
    })
    .toLocal();

  sendAt = localTime.hour;

  if (dayOfWeek) {
    dayOfWeek = localTime.weekday === 7 ? 0 : localTime.weekday;
  }

  if (snakedKeys) {
    return {
      day_of_week: dayOfWeek,
      send_at: sendAt,
      send_email_at: localTime.toISO(),
    };
  } else {
    return { dayOfWeek, sendAt, sendEmailAt: localTime.toISO() };
  }
};

const localToUtc = (dayOfWeek, sendAt, snakedKeys = false) => {
  const utcTime = DateTime.now()
    .toLocal()
    .set({
      weekday: dayOfWeek === 0 ? 7 : dayOfWeek,
      hour: sendAt,
      minute: 0,
      second: 0,
      millisecond: 0,
    })
    .toUTC();

  dayOfWeek = utcTime.weekday === 7 ? 0 : utcTime.weekday;
  sendAt = utcTime.hour;

  if (snakedKeys) {
    return {
      day_of_week: dayOfWeek,
      send_at: sendAt,
      send_email_at: utcTime.toISO(),
    };
  } else {
    return { dayOfWeek, sendAt, sendEmailAt: utcTime.toISO() };
  }
};

const snakeCaseKeys = object =>
  reduce(
    object,
    (result, value, key) => {
      result[snakeCase(key)] = value;
      return result;
    },
    {}
  );

export {
  updateSearch,
  parse,
  queryToFilters,
  filtersToSearch,
  withFormattedDates,
  objectToOptions,
  DATE_PARAMS,
  utcToLocal,
  localToUtc,
  snakeCaseKeys
};
